import { useState } from 'react'
import { Zap } from 'lucide-react'
import { apiRequest } from '@/api/client'
import { usePresalesDashboardStore } from '../store'

interface BatchResult {
  batch_analyzed: number
  batch_errors: number
  total: number
  total_analyzed: number
  remaining: number
}

export function CloseSignalRefreshButton() {
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<string | null>(null)

  const handleRun = async () => {
    setIsRunning(true)
    setLastResult(null)
    setProgress('Starting analysis...')

    try {
      let totalAnalyzed = 0
      let totalErrors = 0
      let total = 0

      // Process in batches until remaining === 0
      while (true) {
        const res = await apiRequest<BatchResult>(
          '/presales-dashboard/close-signals/run-batch',
          { method: 'POST' }
        )

        if (!res.success || !res.data) {
          setLastResult('Failed')
          return
        }

        const { batch_analyzed, batch_errors, remaining } = res.data
        total = res.data.total
        totalAnalyzed += batch_analyzed
        totalErrors += batch_errors

        setProgress(`Analyzing... ${res.data.total_analyzed}/${total} deals`)

        if (remaining === 0) break
      }

      setLastResult(`${totalAnalyzed} analyzed${totalErrors ? `, ${totalErrors} failed` : ''}`)
      usePresalesDashboardStore.getState().loadDeals()
    } catch {
      setLastResult('Error')
    } finally {
      setIsRunning(false)
      setProgress(null)
    }
  }

  return (
    <div className="flex items-center gap-2">
      {(lastResult || progress) && (
        <span className="text-xs text-[var(--text-muted)]">
          {progress || lastResult}
        </span>
      )}
      <button
        onClick={handleRun}
        disabled={isRunning}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg
          bg-[var(--bg-secondary)] text-[var(--text-secondary)]
          border border-[var(--border-color)]
          hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed
          text-sm font-medium
        `}
        title="Re-run AI analysis of HubSpot engagement data for late-stage deals"
      >
        <Zap className={`h-4 w-4 ${isRunning ? 'animate-pulse text-amber-400' : ''}`} />
        {isRunning ? 'Analyzing...' : 'Refresh Close Signals'}
      </button>
    </div>
  )
}
